// P2b-3 §6.2: column-sort state + comparator for the role grids.
//
// Cycle (per Q5 ruling): none → asc → desc → none. Switching to a
// different column starts at asc.
//
// Booleans (Built / Live / Scheduled) treat asc as false-first per Q5.
// Strings use locale-aware compareLocaleCompare; ISO timestamps compare
// lexicographically (parseable to dates by the consumer if needed).

import type { Account } from '../../types/account';
import type { AccountCampaign } from '../../types/accountCampaign';
import type { Campaign } from '../../types/campaign';
import type { CurrentUser } from '../../types/user';
import { formatUserDisplayName } from '../../lib/formatUserDisplayName';
import { resolveGroupTag } from '../../lib/groupTag';

// Common columns + platform-specific extras. Both grids share location,
// am, campaign, startDate; Meta adds metaBuilt + metaLive; Google adds
// googleBuilt + googleScheduled.
export type SortColumn =
  | 'group'
  | 'location'
  | 'am'
  | 'campaign'
  | 'startDate'
  | 'metaBuilt'
  | 'metaLive'
  | 'googleBuilt'
  | 'googleScheduled';

export type SortDirection = 'asc' | 'desc';

export type SortState = { column: SortColumn; direction: SortDirection } | null;

// Default sort: Group ASC, then Location (Account.name) ASC within each
// group; Independent (no-group) rows always sort last. Mirrors the Master
// grid's server canonical ordering so switching grids keeps row order
// stable. 'group' is a default-only sort key — there's no clickable group
// header; a header click switches to single-column sort as before.
export const DEFAULT_SORT: SortState = { column: 'group', direction: 'asc' };

// Step the sort cycle for a click on `column`. See top-of-file note.
export function cycleSort(prev: SortState, column: SortColumn): SortState {
  if (!prev || prev.column !== column) {
    return { column, direction: 'asc' };
  }
  if (prev.direction === 'asc') return { column, direction: 'desc' };
  // 'desc' → none
  return null;
}

// Lookup bundles the sort comparator needs to walk from a row into its
// row → account / campaign / am properties. Pulled from the same maps
// the grid already builds.
export type SortLookups = {
  accountsById: Map<string, Account>;
  campaignsById: Map<string, Campaign>;
  usersById: Map<string, CurrentUser>;
};

function groupNameOf(
  row: AccountCampaign,
  lookups: SortLookups,
): string | null {
  return resolveGroupTag(row.accountId, lookups.accountsById)?.name ?? null;
}

function locationNameOf(
  row: AccountCampaign,
  lookups: SortLookups,
): string | null {
  return lookups.accountsById.get(row.accountId)?.name ?? null;
}

export function compareRows(
  a: AccountCampaign,
  b: AccountCampaign,
  sort: SortState,
  lookups: SortLookups,
): number {
  if (!sort) return 0;
  const dir = sort.direction === 'asc' ? 1 : -1;

  // 'group' is a multi-key sort: group name primary, Account.name
  // secondary (always ASC, "within each group" per spec). No-group
  // (Independent) rows always sort LAST — direction-invariant, decided
  // before the dir multiplier so they never flip to the top on desc.
  if (sort.column === 'group') {
    const ga = groupNameOf(a, lookups);
    const gb = groupNameOf(b, lookups);
    if (ga == null && gb != null) return 1;
    if (ga != null && gb == null) return -1;
    if (ga != null && gb != null) {
      const g = ga.localeCompare(gb);
      if (g !== 0) return g * dir;
    }
    // Same group, or both no-group → secondary by Account.name ASC
    // (matches the 'location' sortKey + Master's canonical sort).
    const la = locationNameOf(a, lookups);
    const lb = locationNameOf(b, lookups);
    if (la == null && lb == null) return 0;
    if (la == null) return 1;
    if (lb == null) return -1;
    return la.localeCompare(lb);
  }

  const va = sortKey(a, sort.column, lookups);
  const vb = sortKey(b, sort.column, lookups);
  return cmp(va, vb) * dir;
}

// Sort key extractors. Strings get locale-aware comparison; nullish
// values sort to the end regardless of direction.
function sortKey(
  row: AccountCampaign,
  col: SortColumn,
  lookups: SortLookups,
): SortKey {
  switch (col) {
    // 'group' is handled as a multi-key sort in compareRows before it
    // reaches sortKey; this case keeps the switch exhaustive over
    // SortColumn and gives a sensible single-key value if ever reused.
    case 'group':
      return groupNameOf(row, lookups);
    case 'location': {
      const acc = lookups.accountsById.get(row.accountId);
      return acc ? acc.name : null;
    }
    case 'am': {
      const u = row.assignedAmId ? lookups.usersById.get(row.assignedAmId) : null;
      return u ? formatUserDisplayName(u) : null;
    }
    case 'campaign': {
      if (row.campaignNameOverride) return row.campaignNameOverride;
      const c = row.campaignId ? lookups.campaignsById.get(row.campaignId) : null;
      return c?.name ?? null;
    }
    case 'startDate':
      return row.startDate ?? null;
    case 'metaBuilt':
      return row.metaBuilt;
    case 'metaLive':
      return row.metaTurnedOn;
    case 'googleBuilt':
      return row.googleBuilt;
    case 'googleScheduled':
      return row.googleScheduled;
  }
}

type SortKey = string | boolean | null | undefined;

function cmp(a: SortKey, b: SortKey): number {
  // Nulls / undefined sort to the end regardless of direction (the
  // direction multiplier is applied by compareRows AFTER cmp returns).
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'boolean' && typeof b === 'boolean') {
    // asc = false first per Q5: false (0) < true (1)
    return a === b ? 0 : a ? 1 : -1;
  }
  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b);
  }
  // Mixed types — fall back to coerced string compare. Shouldn't happen
  // with the sortKey extractors above but keeps the function total.
  return String(a).localeCompare(String(b));
}
